<script setup>
import { VForm } from 'vuetify/components/VForm'
import { formatCurrency, formatDate } from '@core/utils/formatters'
import PageGuidance from '@/components/guidance/PageGuidance.vue'
import PageHeader from '@/components/shared/PageHeader.vue'
import { useTrainerAccountStore } from '@/stores/trainerAccount'
import { usePlatformSubscriptionStore } from '@/stores/platformSubscription'
import StatusChip from '@/components/shared/StatusChip.vue'
import { railsApiDownload } from '@/utils/railsApi'

definePage({
  meta: {
    allowOnboarding: true,
  },
})

const accountStore = useTrainerAccountStore()
const platformStore = usePlatformSubscriptionStore()
const router = useRouter()
const { t } = useI18n()

const profileForm = ref({
  name: '',
  business_name: '',
  timezone: 'Europe/Amsterdam',

  // Dutch is the only product UI language — always persist `nl` (design-system.md §2.6).
  locale: 'nl',
})

const passwordForm = ref({
  currentPassword: '',
  password: '',
  passwordConfirmation: '',
})

const profileError = ref('')
const profileSuccess = ref('')
const passwordError = ref('')
const passwordSuccess = ref('')
const deactivateError = ref('')
const deactivateStep = ref(1)
const acknowledgment = ref(false)
const showDeactivateDialog = ref(false)
const downloadingInvoiceId = ref(null)

const downloadInvoicePdf = async invoice => {
  downloadingInvoiceId.value = invoice.id
  try {
    await railsApiDownload(
      `/api/v1/platform_invoices/${invoice.id}/document`,
      { filename: `${invoice.invoice_number || `factuur-${invoice.id}`}.pdf` },
    )
  }
  catch (err) {
    platformStore.payError = err.payload?.error || err.message || t('platformSubscription.payError')
  }
  finally {
    downloadingInvoiceId.value = null
  }
}


const TIMEZONES = [
  'Europe/Amsterdam',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Paris',
  'UTC',
  'America/New_York',
  'America/Los_Angeles',
]

const profileRef = ref()
const passwordRef = ref()

const lifecycleAlertBorderColor = computed(() => {
  if (accountStore.isActive)
    return 'success'
  if (accountStore.isSuspended)
    return 'warning'
  if (accountStore.isDeactivated)
    return 'secondary'

  return 'info'
})

onMounted(async () => {
  if (!accountStore.trainer)
    await accountStore.fetchProfile().catch(() => accountStore.bootstrap())

  if (accountStore.trainer) {
    profileForm.value = {
      name: accountStore.trainer.name || '',
      business_name: accountStore.trainer.business_name || '',
      timezone: accountStore.trainer.timezone || 'Europe/Amsterdam',
      locale: 'nl',
    }
  }

  if (accountStore.trainer?.can_self_deactivate)
    await accountStore.loadDeactivationPreview().catch(() => null)

  await platformStore.fetch().catch(() => null)
})

const latestFailedPayment = invoice => {
  return (invoice.payments || []).find(payment => payment.lifecycle_state === 'failed') || null
}

const canPayInvoice = invoice => {
  return !!invoice.unsettled && !invoice.payments?.some(payment => payment.lifecycle_state === 'pending')
}

const payInvoice = async invoice => {
  await platformStore.payInvoice(invoice.id).catch(() => null)
}

const retryInvoicePayment = async invoice => {
  const failed = latestFailedPayment(invoice)
  if (!failed)
    return
  await platformStore.retryPayment(failed.id).catch(() => null)
}

const saveProfile = async () => {
  profileError.value = ''
  profileSuccess.value = ''
  try {
    await accountStore.updateProfile(profileForm.value)
    profileSuccess.value = t('account.profileUpdated')
  }
  catch (err) {
    profileError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const onProfileSubmit = () => {
  profileRef.value?.validate().then(({ valid }) => {
    if (valid)
      saveProfile()
  })
}

const savePassword = async () => {
  passwordError.value = ''
  passwordSuccess.value = ''
  try {
    const result = await accountStore.changePassword(passwordForm.value)

    passwordSuccess.value = result.message
    passwordForm.value = {
      currentPassword: '',
      password: '',
      passwordConfirmation: '',
    }
  }
  catch (err) {
    passwordError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const onPasswordSubmit = () => {
  passwordRef.value?.validate().then(({ valid }) => {
    if (valid)
      savePassword()
  })
}

const openDeactivate = async () => {
  deactivateError.value = ''
  deactivateStep.value = 1
  acknowledgment.value = false
  showDeactivateDialog.value = true
  await accountStore.loadDeactivationPreview().catch(() => null)
}

const confirmDeactivate = async () => {
  deactivateError.value = ''
  try {
    await accountStore.deactivateAccount(acknowledgment.value)
    showDeactivateDialog.value = false
    await router.replace({ name: 'login' })
  }
  catch (err) {
    deactivateError.value = err.payload?.errors?.join(' ') || err.message
  }
}
</script>

<template>
  <div>
    <PageHeader
      :title="$t('account.title')"
      :subtitle="$t('account.subtitle')"
    >
      <template #actions>
        <PageGuidance />
      </template>
    </PageHeader>

    <VAlert
      v-if="accountStore.loading && !accountStore.trainer"
      type="info"
      variant="tonal"
      class="mb-4"
      role="status"
    >
      {{ $t('account.loadingAccount') }}
    </VAlert>

    <VAlert
      v-else-if="accountStore.error && !accountStore.trainer"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ accountStore.error }}
    </VAlert>

    <template v-else-if="accountStore.trainer">
      <VAlert
        class="mb-6"
        variant="tonal"
        border="start"
        :border-color="lifecycleAlertBorderColor"
        role="status"
      >
        <div class="d-flex flex-wrap align-center gap-2">
          <span class="text-subtitle-1 font-weight-medium">
            {{ $t('account.stateLabel') }}
          </span>
          <StatusChip
            domain="trainer"
            :state="accountStore.lifecycleState"
          />
        </div>
      </VAlert>

      <VCard class="mb-6">
        <VCardItem>
          <VCardTitle>{{ $t('platformSubscription.title') }}</VCardTitle>
          <VCardSubtitle>{{ $t('platformSubscription.subtitle') }}</VCardSubtitle>
        </VCardItem>
        <VCardText>
          <VAlert
            v-if="platformStore.loading && !platformStore.subscription"
            type="info"
            variant="tonal"
            class="mb-4"
            role="status"
          >
            {{ $t('platformSubscription.loading') }}
          </VAlert>

          <VAlert
            v-else-if="platformStore.error && !platformStore.subscription"
            type="error"
            variant="tonal"
            class="mb-4"
            role="alert"
          >
            {{ platformStore.error }}
          </VAlert>

          <template v-else-if="platformStore.subscription">
            <div class="d-flex flex-wrap align-center gap-2 mb-3">
              <span class="text-body-2 text-medium-emphasis">
                {{ $t('platformSubscription.standingLabel') }}
              </span>
              <StatusChip
                domain="platform_subscription"
                :state="platformStore.subscription.lifecycle_state"
              />
            </div>

            <p class="mb-2">
              <span class="text-medium-emphasis">{{ $t('platformSubscription.planLabel') }}:</span>
              {{ platformStore.subscription.plan_name }}
            </p>

            <p
              v-if="platformStore.subscription.started_at"
              class="mb-2 text-medium-emphasis"
            >
              {{ $t('platformSubscription.startedLabel') }}:
              {{ new Date(platformStore.subscription.started_at).toLocaleDateString('nl-NL') }}
            </p>

            <VAlert
              v-if="platformStore.indefiniteTrial"
              type="info"
              variant="tonal"
              class="mb-4"
              role="status"
            >
              {{ $t('platformSubscription.indefiniteTrial') }}
              <div class="mt-1">
                {{ $t('platformSubscription.trialHint') }}
              </div>
            </VAlert>

            <VAlert
              v-else-if="platformStore.requiresAttention"
              type="warning"
              variant="tonal"
              class="mb-4"
              role="status"
            >
              {{
                platformStore.subscription.lifecycle_state === 'suspended'
                  ? $t('platformSubscription.banner.suspended')
                  : $t('platformSubscription.banner.pastDue')
              }}
            </VAlert>

            <h3 class="text-subtitle-1 mb-2">
              {{ $t('platformSubscription.billingHistoryTitle') }}
            </h3>
            <p class="text-body-2 text-medium-emphasis mb-3">
              {{ $t('platformSubscription.billingHistoryCaption') }}
            </p>

            <VAlert
              v-if="platformStore.payError"
              type="error"
              variant="tonal"
              class="mb-4"
              role="alert"
            >
              {{ platformStore.payError }}
            </VAlert>

            <VAlert
              v-if="platformStore.billingHistoryEmpty"
              type="info"
              variant="tonal"
              role="status"
            >
              {{ $t('platformSubscription.billingHistoryEmpty') }}
            </VAlert>

            <VTable
              v-else
              density="comfortable"
              class="text-no-wrap"
            >
              <thead>
                <tr>
                  <th scope="col">
                    {{ $t('platformSubscription.periodLabel') }}
                  </th>
                  <th scope="col">
                    {{ $t('platformSubscription.amountLabel') }}
                  </th>
                  <th scope="col">
                    {{ $t('platformSubscription.dueLabel') }}
                  </th>
                  <th scope="col">
                    {{ $t('platformSubscription.statusLabel') }}
                  </th>
                  <th scope="col">
                    {{ $t('platformSubscription.actionsLabel') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="invoice in platformStore.billingHistory"
                  :key="invoice.id"
                >
                  <td>
                    {{ formatDate(invoice.period_start_on) }}
                    –
                    {{ formatDate(invoice.period_end_on) }}
                  </td>
                  <td>
                    {{ formatCurrency(invoice.amount_cents, invoice.currency) }}
                  </td>
                  <td>
                    {{ formatDate(invoice.due_on) }}
                  </td>
                  <td>
                    <StatusChip
                      domain="platform_invoice"
                      :state="invoice.lifecycle_state"
                    />
                    <div
                      v-if="latestFailedPayment(invoice)"
                      class="text-caption text-error mt-1"
                    >
                      {{ $t('platformSubscription.paymentFailedHint') }}
                    </div>
                  </td>
                  <td>
                    <div class="d-flex flex-wrap gap-2">
                      <VBtn
                        size="small"
                        variant="text"
                        :loading="downloadingInvoiceId === invoice.id"
                        :aria-label="$t('platformSubscription.downloadPdfAria', { number: invoice.invoice_number || invoice.id })"
                        @click="downloadInvoicePdf(invoice)"
                      >
                        {{ $t('platformSubscription.downloadPdf') }}
                      </VBtn>
                      <VBtn
                        v-if="canPayInvoice(invoice) && !latestFailedPayment(invoice)"
                        size="small"
                        color="primary"
                        variant="tonal"
                        :loading="platformStore.paying"
                        :aria-label="$t('platformSubscription.payAria', { id: invoice.id })"
                        @click="payInvoice(invoice)"
                      >
                        {{ $t('platformSubscription.pay') }}
                      </VBtn>
                      <VBtn
                        v-else-if="latestFailedPayment(invoice) && invoice.unsettled"
                        size="small"
                        color="primary"
                        variant="tonal"
                        :loading="platformStore.paying"
                        :aria-label="$t('platformSubscription.retryAria', { id: invoice.id })"
                        @click="retryInvoicePayment(invoice)"
                      >
                        {{ $t('platformSubscription.retry') }}
                      </VBtn>
                    </div>
                  </td>
                </tr>
              </tbody>
            </VTable>
          </template>
        </VCardText>
      </VCard>

      <VRow>
        <VCol
          cols="12"
          md="6"
        >
          <VCard>
            <VCardItem>
              <VCardTitle>{{ $t('account.businessProfileTitle') }}</VCardTitle>
            </VCardItem>
            <VCardText>
              <VAlert
                v-if="profileError"
                type="error"
                variant="tonal"
                class="mb-4"
                role="alert"
              >
                {{ profileError }}
              </VAlert>
              <VAlert
                v-if="profileSuccess"
                type="success"
                variant="tonal"
                class="mb-4"
                role="status"
              >
                {{ profileSuccess }}
              </VAlert>

              <VForm
                ref="profileRef"
                @submit.prevent="onProfileSubmit"
              >
                <VTextField
                  v-model="profileForm.name"
                  class="mb-4"
                  :label="$t('account.nameLabel')"
                  :rules="[requiredValidator]"
                  :disabled="!accountStore.trainer.can_edit_profile || accountStore.loading"
                />
                <VTextField
                  v-model="profileForm.business_name"
                  class="mb-4"
                  :label="$t('account.businessNameLabel')"
                  :rules="[requiredValidator]"
                  :disabled="!accountStore.trainer.can_edit_profile || accountStore.loading"
                />
                <VSelect
                  v-model="profileForm.timezone"
                  class="mb-4"
                  :label="$t('common.timezone')"
                  :items="TIMEZONES"
                  :rules="[requiredValidator]"
                  :disabled="!accountStore.trainer.can_edit_profile || accountStore.loading"
                />
                <VBtn
                  type="submit"
                  :loading="accountStore.loading"
                  :disabled="!accountStore.trainer.can_edit_profile"
                >
                  {{ $t('account.saveProfile') }}
                </VBtn>
              </VForm>
            </VCardText>
          </VCard>
        </VCol>

        <VCol
          cols="12"
          md="6"
        >
          <VCard class="mb-6">
            <VCardItem>
              <VCardTitle>{{ $t('account.changePasswordTitle') }}</VCardTitle>
            </VCardItem>
            <VCardText>
              <VAlert
                v-if="passwordError"
                type="error"
                variant="tonal"
                class="mb-4"
                role="alert"
              >
                {{ passwordError }}
              </VAlert>
              <VAlert
                v-if="passwordSuccess"
                type="success"
                variant="tonal"
                class="mb-4"
                role="status"
              >
                {{ passwordSuccess }}
              </VAlert>

              <VForm
                ref="passwordRef"
                @submit.prevent="onPasswordSubmit"
              >
                <VTextField
                  v-model="passwordForm.currentPassword"
                  class="mb-4"
                  :label="$t('common.currentPassword')"
                  type="password"
                  autocomplete="current-password"
                  :rules="[requiredValidator]"
                />
                <VTextField
                  v-model="passwordForm.password"
                  class="mb-4"
                  :label="$t('common.newPassword')"
                  type="password"
                  autocomplete="new-password"
                  :rules="[requiredValidator, passwordValidator]"
                />
                <VTextField
                  v-model="passwordForm.passwordConfirmation"
                  class="mb-4"
                  :label="$t('common.confirmNewPassword')"
                  type="password"
                  autocomplete="new-password"
                  :rules="[requiredValidator, confirmedValidator(passwordForm.passwordConfirmation, passwordForm.password)]"
                />
                <VBtn
                  type="submit"
                  :loading="accountStore.loading"
                >
                  {{ $t('common.updatePassword') }}
                </VBtn>
              </VForm>
            </VCardText>
          </VCard>

          <VCard v-if="accountStore.trainer.can_self_deactivate">
            <VCardItem>
              <VCardTitle>{{ $t('account.deactivateTitle') }}</VCardTitle>
            </VCardItem>
            <VCardText>
              <p class="text-medium-emphasis mb-4">
                {{ $t('account.deactivateDescription') }}
              </p>
              <VBtn
                color="error"
                variant="tonal"
                @click="openDeactivate"
              >
                {{ $t('account.deactivateButton') }}
              </VBtn>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </template>

    <VDialog
      v-model="showDeactivateDialog"
      max-width="560"
    >
      <VCard>
        <VCardItem>
          <VCardTitle>
            {{ deactivateStep === 1 ? $t('account.dialog.reviewTitle') : $t('account.dialog.confirmTitle') }}
          </VCardTitle>
        </VCardItem>
        <VCardText>
          <VAlert
            v-if="deactivateError"
            type="error"
            variant="tonal"
            class="mb-4"
            role="alert"
          >
            {{ deactivateError }}
          </VAlert>

          <template v-if="deactivateStep === 1">
            <ul class="mb-4">
              <li
                v-for="(item, index) in accountStore.deactivationPreview?.consequences || []"
                :key="index"
              >
                {{ item }}
              </li>
            </ul>
            <p class="text-medium-emphasis mb-0">
              {{ $t('account.dialog.dependenciesIntro') }}
              {{ accountStore.deactivationPreview?.dependencies?.active_coaching_relationships ?? 0 }} {{ $t('account.dialog.coachingRelationships') }}
              {{ accountStore.deactivationPreview?.dependencies?.unresolved_invoices ?? 0 }} {{ $t('account.dialog.unresolvedInvoices') }}
              {{ accountStore.deactivationPreview?.dependencies?.pending_appointments ?? 0 }} {{ $t('account.dialog.pendingAppointments') }}
            </p>
          </template>

          <template v-else>
            <VCheckbox
              v-model="acknowledgment"
              :label="$t('account.dialog.acknowledgment')"
            />
          </template>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="showDeactivateDialog = false"
          >
            {{ $t('common.cancel') }}
          </VBtn>
          <VBtn
            v-if="deactivateStep === 1"
            color="error"
            @click="deactivateStep = 2"
          >
            {{ $t('account.dialog.continueBtn') }}
          </VBtn>
          <VBtn
            v-else
            color="error"
            :disabled="!acknowledgment"
            :loading="accountStore.loading"
            @click="confirmDeactivate"
          >
            {{ $t('account.dialog.confirmBtn') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
